import { useState, useEffect, useRef, useCallback } from "react";
import { fetchOrderStatus } from "../services/api";

/**
 * Custom hook for order status polling
 * Provides order status and progress with automatic polling
 */
export const useOrderStatus = (orderId, options = {}) => {
	const { pollingInterval = 5000, enabled = true } = options;

	const [status, setStatus] = useState("loading");
	const [progress, setProgress] = useState({
		step: "pending",
		percentage: 0,
		message: "Initializing...",
	});
	const [reportUrl, setReportUrl] = useState(null);
	const [orderInfo, setOrderInfo] = useState({ planType: null, preparingFor: null });
	const [error, setError] = useState(null);
	const [orderError, setOrderError] = useState(null);

	const isMountedRef = useRef(true);
	const intervalIdRef = useRef(null);

	const pollStatus = useCallback(async () => {
		if (!orderId || !isMountedRef.current) return;

		try {
			const data = await fetchOrderStatus(orderId);
			if (!isMountedRef.current) return;

			if (data.success) {
				setStatus(data.status || "loading");
				setError(null);

				// Store order error if available (from metadata)
				if (data.error) {
					setOrderError(data.error);
				} else if (data.status !== "failed") {
					setOrderError(null);
				}

				if (data.progress) {
					setProgress(data.progress);
				}

				if (data.reportUrl) {
					setReportUrl(data.reportUrl);
				}

				if (data.planType || data.preparingFor) {
					setOrderInfo((prev) => ({
						...prev,
						planType: data.planType || prev.planType,
						preparingFor: data.preparingFor || prev.preparingFor,
					}));
				}

				// Stop polling if failed or completed with report URL
				if (
					data.status === "failed" ||
					(data.status === "completed" && data.reportUrl)
				) {
					if (intervalIdRef.current) {
						clearInterval(intervalIdRef.current);
						intervalIdRef.current = null;
					}
				}
			} else {
				throw new Error(data.message || "Failed to fetch order status");
			}
		} catch (err) {
			if (!isMountedRef.current) return;

			const errorMessage =
				err?.response?.data?.message ||
				err?.message ||
				"Failed to fetch order status";
			setError(errorMessage);
			console.error("Polling error:", err);
		}
	}, [orderId]);

	useEffect(() => {
		isMountedRef.current = true;

		if (!orderId || !enabled) {
			setStatus("loading");
			return;
		}

		// Initial poll
		pollStatus();

		// Set up polling interval
		if (enabled && status !== "failed" && !(status === "completed" && reportUrl)) {
			intervalIdRef.current = setInterval(pollStatus, pollingInterval);
		}

		return () => {
			isMountedRef.current = false;
			if (intervalIdRef.current) {
				clearInterval(intervalIdRef.current);
				intervalIdRef.current = null;
			}
		};
	}, [orderId, enabled, pollingInterval, pollStatus, status, reportUrl]);

	return {
		status,
		progress,
		reportUrl,
		orderInfo,
		error, // Polling/network errors
		orderError, // Order-specific error from backend
	};
};

